import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { projectApi } from '@/api/projectApi'
import { request, friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Project } from '@/types/nf'

type Budget = { notices?: { job_id: string; project_budget_gate: string }[]; budget_credits: number | null; spent: number; active_holds: number; committed: number; gate: string; stages: { stage: string; credits: number }[] }

export function ProjectBudgetPanel({ project }: { project: Project }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const [value, setValue] = useState(project.budget_credits == null ? '' : String(project.budget_credits))
  const queryKey = ['project-budget', project.id]
  const budget = useQuery({ queryKey, queryFn: () => request<Budget>(`/v1/projects/${encodeURIComponent(project.id)}/budget`) })
  const parsed = value.trim() === '' ? null : Number(value)
  const valid = parsed === null || (Number.isSafeInteger(parsed) && parsed >= 0)
  const save = useMutation({
    mutationFn: () => projectApi.patch(project.id, { budget_credits: parsed }),
    onSuccess: async () => { await client.invalidateQueries({ queryKey }); await client.invalidateQueries({ queryKey: ['project', project.id] }) },
  })
  return <section className="space-y-3 rounded-md border p-3" aria-label={t('projectBudget.title')}>
    <Label htmlFor={`budget-${project.id}`}>{t('projectBudget.title')}</Label>
    <div className="flex items-center gap-2">
      <Input id={`budget-${project.id}`} type="number" min="0" step="1" value={value} onChange={(event) => setValue(event.target.value)} placeholder={t('projectBudget.unlimited')} aria-invalid={!valid} />
      <Button type="button" variant="outline" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</Button>
    </div>
    <p className="text-xs text-muted-foreground">{t('projectBudget.hint')}</p>
    {!valid ? <p role="alert" className="text-sm text-destructive">{t('projectBudget.invalid')}</p> : null}
    {save.isError || budget.isError ? <p role="alert" className="text-sm text-destructive">{friendlyMessage(save.error || budget.error, t('projectBudget.failed'))}</p> : null}
    {save.isSuccess ? <p role="status" className="text-sm">{t('detail.settingsSaved')}</p> : null}
    {budget.data ? <>
      <p className="text-sm font-mono">{t('projectBudget.usage', { spent: budget.data.spent, active: budget.data.active_holds })}</p>
      {budget.data.gate !== 'green' ? <p role="status" className="text-sm font-medium">{t(budget.data.gate === 'red' ? 'projectBudget.exceeded' : 'projectBudget.warning')}</p> : null}
      {budget.data.notices?.length ? <p role="status" className="text-sm">{t('projectBudget.submissionWarnings', { count: budget.data.notices.length })}</p> : null}
      <ul className="space-y-1 text-sm">{budget.data.stages.map((row) => <li key={row.stage} className="flex justify-between gap-4"><span>{t(`projectBudget.stages.${row.stage}`, { defaultValue: t('projectBudget.stages.other') })}</span><span className="font-mono">{row.credits}</span></li>)}</ul>
    </> : null}
  </section>
}
